/**
 * Interface définissant une liste d'évènements.
 * Cette interface est considéré comme un dictionnaire de fonction, où le nom de la propriété correspond au nom de l'évènement.
 *
 * @example
 * type IMyEvents = {
 *   valueChanged: (newValue: string) => void;
 * }
 */
export type IEvents = Dict<AnyFunction>;

/**
 * Gestionnaire d'abonnement à des évènements.
 *
 * Ce gestionnaire prend en entrée une interface E dont les propriétés listent les noms d'évènement.
 * Détails sur le typage :
 * K extends keyof E : correspond à une propriété de l'interface d'évènements
 * E[K] : signature de la function associée à l'évènement
 *
 * let evtMgr: IListeners<IMyEvents>;
 * evtMgr.on("valueChanged", (newValue: string) => console.log(newValue));
 */
export interface IListeners<E extends IEvents> {

	/**
	 * Ajoute un listener à un évènement
	 *
	 * @param event Nom de l'évènement
	 * @param order Si négatif, exécutés avant les listeners sans ordre, si positifs exécutés après.
	 */
	on<K extends keyof E>(event: K, listener: E[K], order?: number): this;

	/**
	 * Ajoute un listener pour la prochaine émission de l'évènement
	 * En d'autres termes, le listener est enlevé dès la fin de son exécution.
	 *
	 * @param event Nom de l'évènement
	 * @param order Si négatif, exécutés avant les listeners sans ordre, si positifs exécutés après.
	 */
	once<K extends keyof E>(event: K, listener: E[K], order?: number): this;

	/**
	 * Ajoute 1 à N listeners à 1 à N évènements.
	 *
	 * @param events Nom(s) du ou des évènements.
	 * @param listeners Listener ou tableaux des listeners à ajouter.
	 * @param order Si négatif, exécutés avant les listeners sans ordre, si positifs exécutés après.
	 */
	onMany<K extends keyof E>(events: K | K[], listeners: E[K] | E[K][], order?: number): this;

	/**
	 * Enlève un listener à un évènement
	 *
	 * @param event Nom de l'évènement
	 */
	removeListener<K extends keyof E>(event: K, listener: E[K]): this;

	/**
	 * Supprime tous les listeners pour 'event' ou pour tous les events si 'event' n'est pas précisé.
	 *
	 * @param event Nom de l'évènement
	 */
	removeAllListeners<K extends keyof E>(event?: K): this;

	/** Retourne un array ordonné des listeners. Ne retourne jamais null. */
	getListeners<K extends keyof E>(event?: K): E[K][];
}

/**
 * Gestionnaire d'emetteur d'évènements.
 *
 * Ce gestionnaire prend en entrée une interface E dont les propriétés listent les noms d'évènement.
 * Détails sur le typage :
 * K extends keyof E : correspond à une propriété de l'interface d'évènements
 * E[K] : signature de la function associée à l'évènement
 *
 * @example
 * type IMyEvents = {
 *   valueChanged: (newValue: string) => void;
 * }
 *
 * let evtMgr: EventsEmitter<IMyEvents>;
 * evtMgr.emit("valueChanged", "myNewValue");
 */
export interface IEventsEmitter<E extends IEvents> {

	/**
	 * Émet un évènement
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 */
	emit<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void;

	/**
	 * Émet un évènement, en catchant et logant dans la console toute exception
	 * produite par un listener pour ne pas interrompre le processus.
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 */
	emitCatched<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void;

	/**
	 * Émet un évènement de façon asynchrone.
	 * Si un listener retourne une promesse, la résolution de celle-ci est attendue
	 * avant l'éxecution du listener suivant
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 */
	emitAsync<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void;

	/**
	 * Émet un évènement de façon asynchrone, en catchant et logant dans la console toute exception
	 * produite par un listener pour ne pas interrompre le processus.
	 * Si un listener retourne une promesse, la résolution de celle-ci est attendue
	 * avant l'éxecution du listener suivant
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 */
	emitAsyncCatched<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void;

	/**
	 * Émet un évènement jusqu'au 1er listener qui retourne une valeur différente de undefined.
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 * @return le retour du 1er listerner différent de undefined.
	 */
	emitUntil<K extends keyof E>(event: K, ...args: Parameters<E[K]>): any;

	/**
	 * Émet un évènement de façon asynchrone jusqu'au 1er listener qui retourne une valeur différente de undefined.
	 * Si un listener retourne une promesse, la résolution de celle-ci est attendue avant l'exécution du listener suivant.
	 *
	 * @param event Nom de l'évènement.
	 * @param args Paramètres d'appels des listeners de E[K].
	 * @return le retour du 1er listerner différent de undefined et qui n'est pas une promesse.
	 */
	emitAsyncUntil<K extends keyof E>(event: K, ...args: Parameters<E[K]>): Promise<any>;
}

/**
 * Gestionnaire qui combine les fonctions d'abonnement et d'emetteur d'évènements.
 *
 * @example
 * type IMyEvents = {
 *   valueChanged: (newValue: string) => void;
 * }
 *
 * let evtMgr = new EventsMgr<IMyEvents>();
 * evtMgr.on("valueChanged", (newValue: string) => console.log(newValue));
 *
 * evtMgr.emit("valueChanged", "myNewValue");
 */
export interface IEventsMgr<E extends IEvents> extends IListeners<E>, IEventsEmitter<E> {
}

/**
 *
 */
export class EventsMgr<E extends IEvents> implements IEventsMgr<E> {

	_listeners: { [K in keyof E]: EventMgr<any> };

	on<K extends keyof E>(event: K, listener: E[K], order?: number): this {
		if (!this._listeners) this._listeners = Object.create(null);
		let lstns = this._listeners[event];
		if (!lstns) lstns = this._listeners[event] = new EventMgr();
		lstns.add(listener, order);
		return this;
	}

	once<K extends keyof E>(event: K, listener: E[K], order?: number): this {
		const onceLstn = ((...args: any[]) => {
			this.removeListener(event, onceLstn);
			return (listener as any)(...args);
		}) as any as E[K];

		return this.on(event, onceLstn, order);
	}

	onMany<K extends keyof E>(events: K | K[], listeners: E[K] | E[K][], order?: number): this {
		if (Array.isArray(events)) {
			for (const e of events) this.onMany(e, listeners, order);
		} else {
			if (Array.isArray(listeners)) {
				for (const listener of listeners) {
					this.on(events, listener, order);
				}
			} else {
				this.on(events, listeners, order);
			}
		}
		return this;
	}

	removeListener<K extends keyof E>(event: K, listener: E[K]): this {
		const lstns = this._listeners[event];
		if (lstns) lstns.delete(listener);
		return this;
	}

	removeAllListeners<K extends keyof E>(event?: K): this {
		if (event != null) {
			this._listeners[event] = null;
		} else {
			this._listeners = null;
		}
		return this;
	}

	getListeners<K extends keyof E>(event: K): E[K][] {
		if (!this._listeners) return [];
		const lstns = this._listeners[event];
		if (!lstns) return [];
		return lstns.getListeners();
	}

	hasListeners<K extends keyof E>(event: K): boolean {
		if (!this._listeners) return false;
		const lstns = this._listeners[event];
		return lstns && lstns.size > 0;
	}

	hasAnyListeners(): boolean {
		if (!this._listeners) return false;
		for (let k in this._listeners) {
			if (this._listeners[k].size > 0) return true;
		}
		return false;
	}

	emit<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		lstns.emit(...args);
	}

	emitCatched<K extends keyof E>(event: K, ...args: Parameters<E[K]>): void {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		lstns.emitCatched(...args);
	}

	async emitAsync<K extends keyof E>(event: K, ...args: Parameters<E[K]>): Promise<void> {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		return lstns.emitAsync(...args);
	}

	async emitAsyncCatched<K extends keyof E>(event: K, ...args: Parameters<E[K]>): Promise<void> {
			if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		return lstns.emitAsyncCatched(...args);
	}

	emitUntil<K extends keyof E>(event: K, ...args: Parameters<E[K]>): any {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		return lstns.emitUntil(...args);
	}

	emitUntilAfter<K extends keyof E>(event: K, lstn: any, callIt: boolean, ...args: Parameters<E[K]>): any {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		return lstns.emitUntilAfter(lstn, callIt, ...args);
	}

	async emitAsyncUntil<K extends keyof E>(event: K, ...args: Parameters<E[K]>): Promise<any> {
		if (!this._listeners) return;
		const lstns = this._listeners[event];
		if (!lstns) return;
		return lstns.emitAsyncUntil(...args);
	}
}

/**
 * Gestionnaire de listeners pour un seul event.
 */
export class EventMgr<L extends AnyFunction> extends Set<L> {

	/** Ajoute un listener. */
	add(lstn: L, order?: number): this {
		if (!order) {
			if (this._order) {
				const ord = (lstn as any)[this._order];
				if (ord > 0) this._remAfter(lstn);
				else if (ord < 0) this._remBefore(lstn);
			}
			super.add(lstn);
		} else {
			if (!this._order) this._order = Symbol("_order");
			if (order > 0) {
				if (!this.after) this.after = new Set();
				const oldOrd = (lstn as any)[this._order];
				if (!oldOrd) this.delete(lstn);
				else if (oldOrd < 0) this._remBefore(lstn);
				(lstn as any)[this._order] = order;
				this.after.add(lstn);
				this._afterList = null;
			} else {
				if (!this.before) this.before = new Set();
				const oldOrd = (lstn as any)[this._order];
				if (!oldOrd) this.delete(lstn);
				else if (oldOrd > 0) this._remAfter(lstn);
				(lstn as any)[this._order] = order;
				this.before.add(lstn);
				this._beforeList = null;
			}
		}
		return this;
	}

	/** Supprime un listener. */
	delete(lstn: L): boolean {
		const ord = this._order ? (lstn as any)[this._order] : undefined;
		if (!ord) {
			return super.delete(lstn);
		} else if (ord > 0) {
			return this._remAfter(lstn);
		}
		return this._remBefore(lstn);
	}

	/** Elimine tous les listeners de cet EventMgr. */
	clear() {
		super.clear();
		if (this.after) {
			this.after.clear();
			this._afterList = null;
		}
		if (this.before) {
			this.before.clear();
			this._beforeList = null;
		}
	}

	/** Parcourt tous les listeners déclarés. */
	forEach(callbackfn: (value: L, value2: L, set: Set<L>) => void, thisArg?: any): void {
		if (this.before) this.beforeList.forEach((value: L, idx: number, arr: L[]) => {callbackfn.call(thisArg, value, value, this)}, thisArg);
		super.forEach(callbackfn, thisArg);
		if (this.after) this.afterList.forEach((value: L, idx: number, arr: L[]) => {callbackfn.call(thisArg, value, value, this)}, thisArg);
	}

	/** Evalue si ce listerner est présent. */
	has(lstn: L): boolean {
		const ord = this._order ? (lstn as any)[this._order] : undefined;
		if (!ord) {
			return super.has(lstn);
		} else if (ord > 0) {
			return this.after ? this.after.has(lstn) : false;
		}
		return this.before ? this.before.has(lstn) : false;
	}

	get size(): number {
		let s = super.size;
		if (this.after) s += this.after.size;
		if (this.before) s += this.before.size;
		return s;
	}

	[Symbol.iterator](): IterableIterator<L> {
		return this.getListeners()[Symbol.iterator]();
	}

	/** Retourne une copie du tableau de tous les listeners. */
	getListeners(): L[] {
		const result = [];
		if (this.before) result.push(...this.before);
		result.push(...super.keys());
		if (this.after) result.push(...this.after);
		return result;
	}

	emit(...args: Parameters<L>): void {
		if (this.before) for (const l of this.beforeList) l(...args);
		for (const l of super.keys()) l(...args);
		if (this.after) for (const l of this.afterList) l(...args);
	}

	emitCatched(...args: Parameters<L>): void {
		if (this.before) for (const l of this.beforeList) try {l(...args)} catch (e) {console.error(e)}
		for (const l of super.keys()) try { l(...args)} catch (e) {console.error(e)}
		if (this.after) for (const l of this.afterList) try {l(...args)} catch (e) {console.error(e)}
	}

	async emitAsync(...args: Parameters<L>): Promise<void> {
		if (this.before) for (const l of this.beforeList) {
			const r = l(...args);
			if (r instanceof Promise) await r;
		}
		for (const l of super.keys()) {
			const r = l(...args);
			if (r instanceof Promise) await r;
		}
		if (this.after) for (const l of this.afterList) {
			const r = l(...args);
			if (r instanceof Promise) await r;
		}
	}

	async emitAsyncCatched(...args: Parameters<L>): Promise<void> {
		if (this.before) for (const l of this.beforeList) {
			try {
				const r = l(...args);
				if (r instanceof Promise) await r;
			} catch (e) {
				console.error(e);
			}
		}
		for (const l of super.keys()) {
			try {
				const r = l(...args);
				if (r instanceof Promise) await r;
			} catch (e) {
				console.error(e);
			}
		}
		if (this.after) for (const l of this.afterList) {
			try {
				const r = l(...args);
				if (r instanceof Promise) await r;
			} catch (e) {
				console.error(e);
			}
		}
	}

	emitUntil(...args: Parameters<L>): any {
		if (this.before) for (const l of this.beforeList) {
			const r = l(...args);
			if (r !== undefined) return r;
		}
		for (const l of super.keys()) {
			const r = l(...args);
			if (r !== undefined) return r;
		}
		if (this.after) for (const l of this.afterList) {
			const r = l(...args);
			if (r !== undefined) return r;
		}
	}

	emitUntilAfter(lstn: any, callIt: boolean, ...args: Parameters<L>): any {
		let found = false;
		if (this.before) for (const l of this.beforeList) {
			if (found) {
				const r = l(...args);
				if (r !== undefined) return r;
			} else if (l === lstn) {
				if (callIt) {
					const r = l(...args);
					if (r !== undefined) return r;
				}
				found = true;
			}
		}
		for (const l of super.keys()) {
			if (found) {
				const r = l(...args);
				if (r !== undefined) return r;
			} else if (l === lstn) {
				if (callIt) {
					const r = l(...args);
					if (r !== undefined) return r;
				}
				found = true;
			}
		}
		if (this.after) for (const l of this.afterList) {
			if (found) {
				const r = l(...args);
				if (r !== undefined) return r;
			} else if (l === lstn) {
				if (callIt) {
					const r = l(...args);
					if (r !== undefined) return r;
				}
				found = true;
			}
		}
	}

	async emitAsyncUntil(...args: Parameters<L>): Promise<any> {
		if (this.before) for (const l of this.beforeList) {
			let r = l(...args);
			if (r instanceof Promise) r = await r;
			if (r !== undefined) return r;
		}
		for (const l of super.keys()) {
			let r = l(...args);
			if (r instanceof Promise) r = await r;
			if (r !== undefined) return r;
		}
		if (this.after) for (const l of this.afterList) {
			let r = l(...args);
			if (r instanceof Promise) r = await r;
			if (r !== undefined) return r;
		}
	}

	//*** interne ***

	protected before: Set<L>;
	protected _beforeList: Array<L>;

	protected after: Set<L>;
	protected _afterList: Array<L>;

	protected _order: symbol;

	/** Liste des listeners before triée dans l'ordre des priorités. */
	protected get beforeList(): L[] {
		if (!this._beforeList) {
			this._beforeList = [...this.before].sort((l1: any, l2: any) => {return l1[this._order] - l2[this._order]});
		}
		return this._beforeList
	}

	/** Liste des listeners after triée dans l'ordre des priorités. */
	protected get afterList(): L[] {
		if (!this._afterList) {
			this._afterList = [...this.after].sort((l1: any, l2: any) => {return l1[this._order] - l2[this._order]});
		}
		return this._afterList
	}

	protected _remBefore(listener: any): boolean {
		if (this.before) {
			delete listener[this._order];
			if (this.before.delete(listener)) {
				this._beforeList = null;
				return true;
			}
		}
		return false;
	}

	protected _remAfter(listener: any) {
		if (this.after) {
			delete listener[this._order];
			if (this.after.delete(listener)) {
				this._afterList = null;
				return true;
			}
		}
		return false;
	}
}


/**
 * Proxy vers un autre IEventsMgr<E>.
 *
 * Permet d'éliminer tous les listeners inscrit via ce proxy
 * en un seul appel via EventMgrProxy.removeAllListeners() : résoud des problèmes de fuites mémoire.
 *
 * Si des events sont émis à partir de ce proxy, seuls les listeners inscrits via ce proxy
 * sont notifiés.
 */
export class EventsMgrProxy<E extends IEvents> extends EventsMgr<E> {

	constructor(public target: IEventsMgr<E>) {
		super();
	}

	on<K extends keyof E>(event: K, listener: E[K], order?: number): this {
		this.target.on(event, listener, order);
		super.on(event, listener, order);
		return this;
	}

	removeListener<K extends keyof E>(event: K, listener: E[K]): this {
		this.target.removeListener(event, listener);
		super.removeListener(event, listener);
		return this;
	}

	removeAllListeners<K extends keyof E>(event?: K): this {
		if (event == null) {
			for (const ev in this._listeners) {
				const set = this._listeners[ev];
				for (const lstn of set) {
					this.target.removeListener(ev, lstn);
				}
			}
			this._listeners = null;
		} else {
			const set = this._listeners[event];
			if (set) {
				for (const lstn of set) {
					this.target.removeListener(event, lstn);
				}
				delete this._listeners[event];
			}
		}
		return this;
	}

	switchTarget<K extends keyof E>(newTarget: IEventsMgr<E>, removeLstnOnOld?: boolean) {
		for (const event in this._listeners) {
			const lstns = this._listeners[event];
			for (const l of lstns) {
				if (removeLstnOnOld) this.target.removeListener(event, l);
				newTarget.on(event, l);
			}
		}
		this.target = newTarget;
	}
}


export class EventsMgrCb<E extends IEvents> extends EventsMgr<E> {

	constructor(public cb: () => void) {super();}

	on<K extends keyof E>(event: K, listener: E[K], order?: number): this {
		super.on(event, listener, order);
		this.cb();
		return this;
	}

	removeListener<K extends keyof E>(event: K, listener: E[K]): this {
		super.removeListener(event, listener);
		this.cb();
		return this;
	}

	removeAllListeners<K extends keyof E>(event?: K): this {
		super.removeAllListeners(event);
		this.cb();
		return this;
	}
}